import React, { useEffect, useState } from "react";
import styles from "./CustomerInformation.module.scss";
import { ATMGrid } from "../../../components/atoms/ATMGrid/ATMGrid.component";
import { ATMSegment } from "../../../components/atoms/ATMSegment/ATMSegment.component";
import { ExpandMore, ExpandLess, Description, AddCircle } from "@mui/icons-material";
import { ATMHeader } from "../../../components/atoms/ATMHeader/ATMHeader.component";
import CustomerInformationConstants from "constants/CustomerInformationConstants/CustomerInformationConstants";
import { applicationData, FumigationRecord, EMPTY_RECORD } from "contexts/applicationDataFieldTypes";
import useFetchAddress from "hooks/useFetchAddress";
import {
    INITIAL_ROWS,
    RowAddressState,
    TH_STYLE,
    TD_STYLE,
    INPUT_STYLE,
    SELECT_STYLE,
    ORDER_SCHEDULE_OPTIONS,
    TIME_OPTIONS,
    RESTORE_TIME_OPTIONS,
    CLOSE_DATE_OPTIONS,
    RESTORE_DATE_OPTIONS,
    ACCESS_ARRANGEMENTS_OPTIONS,
    CODE_TYPE_OPTIONS,
} from "./customerInformation.types";
import { RecordFieldKey } from "../validation/fumigationValidation";

interface CustomerInformationProps {
    applicationData: applicationData;
    setApplicationData: React.Dispatch<React.SetStateAction<applicationData>>;
    toggle: boolean;
    setToggle: (value: boolean) => void;
    control: any;
    recordFieldErrors?: Record<number, Partial<Record<RecordFieldKey, string>>>;
    /** Section-level messages (e.g. "please provide at least 1 row") to show at the top of the segment. */
    lineErrors?: string[];
    sectionRef?: React.RefObject<HTMLDivElement>;
}

/** Red outline applied to cells that failed validation. */
const ERROR_OUTLINE: React.CSSProperties = {
    border: '1px solid #A11003',
    backgroundColor: '#FDF3F2',
};

const ROW_ERROR_TEXT: React.CSSProperties = {
    fontSize: '11px',
    fontFamily: 'Arial, Helvetica, sans-serif',
    color: '#A11003',
};

const CustomerInformation: React.FC<CustomerInformationProps> = ({
    applicationData,
    setApplicationData,
    toggle,
    setToggle,
    recordFieldErrors = {},
    lineErrors = [],
    sectionRef,
}) => {
    const [rows, setRows] = useState<FumigationRecord[]>(() => {
        const existing = (applicationData.records || []).map(r => ({
            ...EMPTY_RECORD,
            ...r,
        }));
        const padded = [...existing];
        while (padded.length < INITIAL_ROWS) {
            padded.push({ ...EMPTY_RECORD, id: Date.now() + padded.length });
        }
        return padded;
    });

    const [addressState, setAddressState] = useState<RowAddressState[]>(
        () => rows.map(r => ({ options: r.premiseAddress ? [r.premiseAddress] : [], query: r.premiseAddress || '' }))
    );

    const { mutate: fetchAddress, data: addressData, isLoading: isAddressLoading } = useFetchAddress();
    const [activeAddressRow, setActiveAddressRow] = useState<number | null>(null);

    useEffect(() => {
        if (activeAddressRow === null || !addressData?.items) return;
        const newOptions = addressData.items.map(item => item.label);
        setAddressState(prev => {
            const updated = [...prev];
            updated[activeAddressRow] = { ...updated[activeAddressRow], options: newOptions };
            return updated;
        });
    }, [addressData]);

    useEffect(() => {
        setApplicationData(prev => ({ ...prev, records: rows }));
    }, [rows]);

    const handleFieldChange = (rowIndex: number, field: keyof FumigationRecord, value: string | number) => {
        setRows(prev => {
            const updated = [...prev];
            updated[rowIndex] = { ...updated[rowIndex], [field]: value };
            return updated;
        });
    };

    const handleAddressSearch = (rowIndex: number, query: string) => {
        setAddressState(prev => {
            const updated = [...prev];
            updated[rowIndex] = { ...updated[rowIndex], query };
            return updated;
        });
        handleFieldChange(rowIndex, 'premiseAddress', query);
        if (query && /\s.+/.test(query)) {
            setActiveAddressRow(rowIndex);
            fetchAddress(query);
        }
    };

    const handleAddRow = () => {
        setRows(prev => [...prev, { ...EMPTY_RECORD, id: Date.now() + Math.random() }]);
        setAddressState(prev => [...prev, { options: [], query: '' }]);
    };

    const handleClearRow = (rowIndex: number) => {
        setRows(prev => {
            const updated = [...prev];
            updated[rowIndex] = { ...EMPTY_RECORD, id: updated[rowIndex].id };
            return updated;
        });
        setAddressState(prev => {
            const updated = [...prev];
            updated[rowIndex] = { options: [], query: '' };
            return updated;
        });
    };

    const handleToggle = () => setToggle(!toggle);

    /** Merges the error outline into a cell's input style when that field failed. */
    const withError = (
        base: React.CSSProperties,
        rowIndex: number,
        field: RecordFieldKey,
    ): React.CSSProperties =>
        recordFieldErrors[rowIndex]?.[field] ? { ...base, ...ERROR_OUTLINE } : base;

    const hasError = (rowIndex: number, field: RecordFieldKey): boolean =>
        Boolean(recordFieldErrors[rowIndex]?.[field]);

    /** All messages for a row, joined for the row-level tooltip / caption. */
    const rowMessages = (rowIndex: number): string[] =>
        Object.values(recordFieldErrors[rowIndex] ?? {}).filter(Boolean) as string[];

    // Split the access note label around the highlighted phrase
    const [accessNotePre, accessNotePost] = CustomerInformationConstants.LABEL_ADDITIONAL_ACCESS_NOTE
        .split('"Please Cancel" or "Thank you"');

    return (
        <div ref={sectionRef}>
            <ATMSegment size="tiny" className={styles.commonMargin}>
                <ATMGrid>
                <ATMGrid.Row>
                    <ATMGrid.Column width={8} className={styles.segmentsStyle} onClick={handleToggle}>
                        <ATMHeader as="h2" className={styles.headerStyle}>
                            <Description />
                            {CustomerInformationConstants.SECTION_TITLE}
                        </ATMHeader>
                    </ATMGrid.Column>
                    <ATMGrid.Column width={8} floated="right" textAlign="right" onClick={handleToggle}>
                        {toggle ? <ExpandLess /> : <ExpandMore />}
                    </ATMGrid.Column>
                </ATMGrid.Row>

                {toggle && (
                    <>
                        {/* Section-level validation errors (e.g. "at least 1 row") */}
                        {lineErrors.length > 0 && (
                            <ATMGrid.Row>
                                <ATMGrid.Column width={16}>
                                    <div role="alert" style={{
                                        fontSize: '13px',
                                        fontFamily: 'Arial, Helvetica, sans-serif',
                                        border: '1px solid #A11003',
                                        borderLeft: '4px solid #A11003',
                                        borderRadius: '4px',
                                        background: '#FDF3F2',
                                        padding: '10px 14px',
                                        marginBottom: '6px',
                                    }}>
                                        <strong style={{ color: '#A11003', display: 'block', marginBottom: '4px' }}>
                                            Please correct the following:
                                        </strong>
                                        <ul style={{ margin: 0, paddingLeft: '18px', color: '#A11003' }}>
                                            {lineErrors
                                                .filter((v, i, arr) => arr.indexOf(v) === i)
                                                .map((msg, i) => <li key={i}>{msg}</li>)}
                                        </ul>
                                    </div>
                                </ATMGrid.Column>
                            </ATMGrid.Row>
                        )}

                        {/* Hours info */}
                        <ATMGrid.Row>
                            <ATMGrid.Column width={16}>
                                <div className={styles.hoursGrid}>
                                    {/* Close row */}
                                    <div className={styles.hoursRow}>
                                        <span className={styles.hoursLabel}>Close:</span>
                                        <span className={styles.hoursValue}>{CustomerInformationConstants.HOURS.Close[0]}</span>
                                        <span className={styles.hoursValue}>{CustomerInformationConstants.HOURS.Close[1]}</span>
                                        <span className={styles.hoursValue}>{CustomerInformationConstants.HOURS.Close[2]}</span>
                                        <span className={styles.hoursCancellationLabel}>Cancellation:</span>
                                    </div>
                                    {/* Restore row */}
                                    <div className={styles.hoursRow}>
                                        <span className={styles.hoursLabel}>Restore:</span>
                                        <span className={styles.hoursValue}>{CustomerInformationConstants.HOURS.Restore[0]}</span>
                                        <span className={styles.hoursValue}>{CustomerInformationConstants.HOURS.Restore[1]}</span>
                                        <span className={styles.hoursValue}>{CustomerInformationConstants.HOURS.Restore[2]}</span>
                                        <span className={styles.hoursCancellationValue}>{CustomerInformationConstants.HOURS.Cancellation[0]}</span>
                                    </div>
                                    {/* Saturday Restore row */}
                                    <div className={styles.hoursRow}>
                                        <span className={styles.hoursLabel}>Saturday Restore:</span>
                                        <span className={styles.hoursValue}>{CustomerInformationConstants.HOURS['Saturday Restore'][0]}</span>
                                    </div>
                                </div>
                            </ATMGrid.Column>
                        </ATMGrid.Row>

                        {/* Data entry table */}
                        <ATMGrid.Row>
                            <ATMGrid.Column width={16} style={{ overflowX: 'auto' }}>
                                <table className={styles.dataTable}>
                                    <thead>
                                        <tr>
                                            <th style={TH_STYLE}>&nbsp;</th>
                                            <th style={TH_STYLE}>&nbsp;</th>
                                            <th style={TH_STYLE}>Customer Name</th>
                                            <th style={TH_STYLE}>Premise Address</th>
                                            <th style={TH_STYLE}>Order Type</th>
                                            <th style={TH_STYLE}>Close Schedule</th>
                                            <th style={TH_STYLE}>Close Time</th>
                                            <th style={TH_STYLE}>Restore Schedule</th>
                                            <th style={TH_STYLE}>Restore Time</th>
                                            <th style={TH_STYLE}>Total Meter</th>
                                            <th style={TH_STYLE}>Access Arrangements</th>
                                            <th style={TH_STYLE}>Code Type</th>
                                            <th style={TH_STYLE}>Code</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, i) => (
                                            <tr key={row.id ?? i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                                                <td style={TD_STYLE} data-label="">
                                                    {/* Legacy error.gif / noerror.gif indicator */}
                                                    <span
                                                        style={{
                                                            fontSize: '10px',
                                                            color: rowMessages(i).length ? '#A11003' : '#ccc',
                                                        }}
                                                        title={rowMessages(i).join('; ')}
                                                    >
                                                        &#9679;
                                                    </span>
                                                </td>
                                                <td style={{ ...TD_STYLE, color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
                                                    data-label="Row"
                                                    title="Clear Line"
                                                    onClick={() => handleClearRow(i)}>
                                                    {String(i + 1).padStart(2, '0')}
                                                </td>
                                                <td style={TD_STYLE} data-label="Customer Name">
                                                    <input type="text" maxLength={30} style={{ ...INPUT_STYLE, width: '120px', maxWidth: '100%' }}
                                                        value={row.customerName}
                                                        onChange={e => handleFieldChange(i, 'customerName', e.target.value.toUpperCase())}
                                                        autoComplete="off" aria-label={`Row ${i + 1} Customer Name`} />
                                                </td>
                                                <td style={TD_STYLE} data-label="Premise Address">
                                                    <div className={styles.addressInputWrapper}>
                                                        <input type="text" maxLength={80} style={{ ...INPUT_STYLE, width: '200px', maxWidth: '100%' }}
                                                            value={addressState[i]?.query ?? row.premiseAddress}
                                                            onChange={e => handleAddressSearch(i, e.target.value.toUpperCase())}
                                                            list={`addr-${i}`} autoComplete="off" aria-label={`Row ${i + 1} Premise Address`} />
                                                        {isAddressLoading && activeAddressRow === i && (
                                                            <span
                                                                className={styles.addressSpinner}
                                                                role="status"
                                                                aria-label="Searching addresses"
                                                            />
                                                        )}
                                                    </div>
                                                    <datalist id={`addr-${i}`}>
                                                        {(addressState[i]?.options ?? []).map(opt => <option key={opt} value={opt} />)}
                                                    </datalist>
                                                </td>
                                                <td style={TD_STYLE} data-label="Order Type">
                                                    <select style={{ ...SELECT_STYLE, width: '70px', maxWidth: '100%' }} value={row.aer}
                                                        onChange={e => handleFieldChange(i, 'aer', e.target.value)}
                                                        aria-label={`Row ${i + 1} Order Type`}>
                                                        {ORDER_SCHEDULE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                    </select>
                                                </td>
                                                <td style={TD_STYLE} data-label="Close Schedule">
                                                    <select style={withError({ ...SELECT_STYLE, width: '90px', maxWidth: '100%' }, i, 'closeSchedule')} value={row.closeSchedule}
                                                        onChange={e => handleFieldChange(i, 'closeSchedule', e.target.value)}
                                                        aria-label={`Row ${i + 1} Close Date`}
                                                        aria-invalid={hasError(i, 'closeSchedule')}>
                                                        {CLOSE_DATE_OPTIONS.map(o => <option key={o.value || 'none'} value={o.value}>{o.label}</option>)}
                                                    </select>
                                                </td>
                                                <td style={TD_STYLE} data-label="Close Time">
                                                    <select style={{ ...SELECT_STYLE, width: '60px', maxWidth: '100%' }} value={row.closeTime}
                                                        onChange={e => handleFieldChange(i, 'closeTime', e.target.value)}
                                                        aria-label={`Row ${i + 1} Close Time`}>
                                                        {TIME_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                    </select>
                                                </td>
                                                <td style={TD_STYLE} data-label="Restore Schedule">
                                                    <select style={withError({ ...SELECT_STYLE, width: '90px', maxWidth: '100%' }, i, 'restoreSchedule')} value={row.restoreSchedule}
                                                        onChange={e => handleFieldChange(i, 'restoreSchedule', e.target.value)}
                                                        aria-label={`Row ${i + 1} Restore Date`}
                                                        aria-invalid={hasError(i, 'restoreSchedule')}>
                                                        {RESTORE_DATE_OPTIONS.map(o => <option key={o.value || 'none'} value={o.value}>{o.label}</option>)}
                                                    </select>
                                                </td>
                                                <td style={TD_STYLE} data-label="Restore Time">
                                                    <select style={{ ...SELECT_STYLE, width: '60px', maxWidth: '100%' }} value={row.restoreTime}
                                                        onChange={e => handleFieldChange(i, 'restoreTime', e.target.value)}
                                                        aria-label={`Row ${i + 1} Restore Time`}>
                                                        {RESTORE_TIME_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                    </select>
                                                </td>
                                                <td style={TD_STYLE} data-label="Total Meter">
                                                    <input type="text" maxLength={2} style={withError({ ...INPUT_STYLE, width: '50px', maxWidth: '100%' }, i, 'qty')}
                                                        value={row.qty}
                                                        onChange={e => handleFieldChange(i, 'qty', Number(e.target.value) || 0)}
                                                        autoComplete="off" aria-label={`Row ${i + 1} Total Meter`}
                                                        aria-invalid={hasError(i, 'qty')} />
                                                </td>
                                                <td style={TD_STYLE} data-label="Access Arrangements">
                                                    <select style={{ ...SELECT_STYLE, width: '140px', maxWidth: '100%' }} value={row.accessArrangements}
                                                        onChange={e => handleFieldChange(i, 'accessArrangements', e.target.value)}
                                                        aria-label={`Row ${i + 1} Access Arrangements`}>
                                                        {ACCESS_ARRANGEMENTS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                    </select>
                                                </td>
                                                <td style={TD_STYLE} data-label="Code Type">
                                                    <select style={withError({ ...SELECT_STYLE, width: '90px', maxWidth: '100%' }, i, 'codeType')} value={row.codeType}
                                                        onChange={e => handleFieldChange(i, 'codeType', e.target.value)}
                                                        aria-label={`Row ${i + 1} Code Type`}
                                                        aria-invalid={hasError(i, 'codeType')}>
                                                        {CODE_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                    </select>
                                                </td>
                                                <td style={TD_STYLE} data-label="Code">
                                                    <input type="text" maxLength={8} style={withError({ ...INPUT_STYLE, width: '70px', maxWidth: '100%' }, i, 'code')}
                                                        value={row.code}
                                                        onChange={e => handleFieldChange(i, 'code', e.target.value)}
                                                        autoComplete="off" aria-label={`Row ${i + 1} Code`}
                                                        aria-invalid={hasError(i, 'code')} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <div style={{ marginTop: '8px' }}>
                                    <button type="button" onClick={handleAddRow} style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                        fontSize: '11px', fontFamily: 'Arial, Helvetica, sans-serif',
                                        color: '#001A70', background: 'none', border: '1px solid #001A70',
                                        borderRadius: '4px', padding: '3px 10px', cursor: 'pointer',
                                    }}>
                                        <AddCircle style={{ fontSize: '14px' }} />
                                        Add Row
                                    </button>
                                </div>
                            </ATMGrid.Column>
                        </ATMGrid.Row>

                        {/* Additional Access Info */}
                        <ATMGrid.Row>
                            <ATMGrid.Column width={16}>
                                {/* Access note — wraps on mobile */}
                                <div className={styles.accessNote}>
                                    <span className={styles.accessNoteBlue}>{accessNotePre}</span>
                                    <span className={styles.accessNoteRed}>&quot;Please Cancel&quot; or &quot;Thank you&quot;</span>
                                    <span className={styles.accessNoteBlue}>{accessNotePost}</span>
                                </div>
                                {/* Textarea row — stacks on mobile */}
                                <div className={styles.accessTextareaRow}>
                                    <span className={styles.accessTextareaLabel}>
                                        Additional Access / Address Information :
                                    </span>
                                    <textarea
                                        rows={2}
                                        className={styles.accessTextarea}
                                        value={applicationData.additionalAccessInformation}
                                        onChange={e => setApplicationData(prev => ({ ...prev, additionalAccessInformation: e.target.value }))}
                                        aria-label="Additional Access / Address Information"
                                    />
                                </div>
                            </ATMGrid.Column>
                        </ATMGrid.Row>
                    </>
                )}
            </ATMGrid>
        </ATMSegment>
        </div>
    );
};

export default CustomerInformation;
